import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { WarningMessageComponent, WarningMessage } from '../../common/warning-message/warning-message.component';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { pickRole22SchemaFile } from '../../../services/mbrs/validators/role22-notes-list-of-notes.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: number;
    note?: string;
    rows?: number;
    options?: string[];
    conditionalOn?: string;
    conditionalValue?: string;
    placeholder?: string;
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role22-730000-notes-list-of-notes',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputText,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    WarningMessageComponent,
    PlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role22-730000-notes-list-of-notes.component.html',
  styleUrls: ['./role22-730000-notes-list-of-notes.component.css']
})
export class Role22730000NotesListOfNotesComponent implements OnInit, OnDestroy {
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  form!: FormGroup;
  schema?: FormSchema;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Notes - List of Notes (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.notesListOfNotes
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  isLoading = true;
  private isLoadingState = false;

  /**
   * Get total number of actual form fields (excluding section headers)
   */
  get totalFields(): number {
    if (!this.schema) return 0;
    return this.schema.controls.filter(control => control.type !== 'SectionHeader').length;
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '22',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupWarningEvaluation();
      this.evaluateFieldWarnings();

      // Reveal the sheet's already-computed errors now that the form exists and
      // has been populated. Deterministic replacement for the filing-form
      // container's setTimeout-driven markAllAsTouched, which silently no-ops
      // when a sheet's async init has not yet assigned its FormGroup — see
      // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
      revealValidationErrors(this.form, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.notesListOfNotes),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((notesListOfNotesData) => {
        if (notesListOfNotesData && Object.keys(notesListOfNotesData).length > 0) {
          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // NO date conversion needed for this form - all fields are TextBlock

          // Patch the form with the new data
          this.form.patchValue(notesListOfNotesData, { emitEvent: false });

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load schema from JSON file (dynamically selects MFRS or MPERS)
   */
  private async loadSchema(): Promise<void> {
    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role22');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Get the accounting standard from filing info
    const filingData = this.stateService.getActiveFilingInfoData();
    const accountingStandard = filingData['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';

    // Determine which schema file to load (MFRS default, MPERS when the
    // accounting-standard literal matches). Selection logic is shared
    // with role22NotesListOfNotesValidator via pickRole22SchemaFile so
    // both paths cannot drift apart.
    const schemaFile = pickRole22SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      `/assets/forms/${schemaFile}`,
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    const formControls: any = {};

    if (this.schema && this.schema.controls) {
      this.schema.controls.forEach(control => {
        if (control.type !== 'SectionHeader') {
          const defaultValue = this.getDefaultValue(control);
          const validators: any[] = this.getValidators(control);
          formControls[control.qname] = new FormControl(defaultValue, validators);
        }
      });
    }

    this.form = new FormGroup(formControls);
  }

  /**
   * Get default value for control based on type
   */
  private getDefaultValue(control: SchemaControl): any {
    switch (control.type) {
      case 'YesNoItemType':
        return null;
      case 'IntegerItemType':
        return null;
      case 'TextBlock':
      case 'StringItemType':
      default:
        return '';
    }
  }

  /**
   * Get validators for control
   */
  private getValidators(control: SchemaControl): any[] {
    const validators: any[] = [];
    return validators;
  }

  /**
   * Check if field should be displayed based on conditional logic
   */
  shouldDisplayField(control: SchemaControl): boolean {
    if (!control.ui.conditionalOn) {
      return true;
    }

    const parentControl = this.form?.get(control.ui.conditionalOn);
    if (!parentControl) {
      return true;
    }

    return parentControl.value === control.ui.conditionalValue;
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.notesListOfNotes);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.notesListOfNotes);
    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });
    }
    // Small delay to ensure form is fully initialized before enabling auto-save
    setTimeout(() => {
      this.isLoadingState = false;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        // Re-baseline the transformed form value (esp. the rich-text field's on-load
        // fragment normalization) so the snapshot equals what the auto-save persist
        // writes; without it a full revert never cleared the pencil.
        this.stateService.updateSheetData(
          SHEET_KEYS.notesListOfNotes,
          this.form.value,
          this.form.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
        this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.notesListOfNotes, false);
      }, 600);
    }, 100);
  }

  /**
   * Setup automatic state persistence
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        // Save with hardcoded valid=true: Role-22 has all optional TextBlock fields,
        // so we always treat the sheet as valid regardless of Angular's validity flag.
        this.stateService.updateSheetData(SHEET_KEYS.notesListOfNotes, this.form.value, true);
      },
      evaluateStatus: () => {
        // Hardcoded hasErrors: false — all fields are optional, no errors possible.
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.notesListOfNotes, { hasErrors: false });
      },
    });
  }

  /**
   * Auto-fill demo data
   */
  autoFillDemo(): void {
    if (!this.schema) return;

    const demoData: any = {};

    // Fill first 5 TextBlock fields with sample content for demonstration
    let count = 0;
    for (const control of this.schema.controls) {
      if (control.type === 'TextBlock' && count < 5) {
        const cleanLabel = control.label.replace(' [text block]', '');
        demoData[control.qname] = `This is sample disclosure content for ${cleanLabel}.\n\nThe note would contain detailed information about ${cleanLabel.toLowerCase()}.\n\nThis content is auto-filled for demonstration purposes only.`;
        count++;
      }
    }

    this.form.patchValue(demoData);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: `Filled first 5 note disclosures with sample data`,
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    if (confirm('Are you sure you want to clear all data in this form?')) {
      this.form.reset();
      this.stateService.clearSheetData(SHEET_KEYS.notesListOfNotes);

      this.messageService.add({
        severity: 'info',
        summary: 'Form Cleared',
        detail: 'All form data has been cleared',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_DEFAULT_MS
      });
      return;
    }

    const formData = this.form.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'List of notes has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.form.get(qname);
  }

  /**
   * Setup warning evaluation for all fields with warnings defined in schema
   */
  private setupWarningEvaluation(): void {
    if (!this.schema?.controls) return;

    // Re-evaluate warnings whenever form values change
    this.form.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // notesListOfNotes slice, `applyRemoteChange` rehydrates the shared
    // state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges), which is what makes the local
    // valueChanges-driven re-evaluation above moot here — the field-level
    // warning indicators are refreshed as part of adoption instead.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.notesListOfNotes)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesListOfNotes)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.form, this.stateService.getSheetData(SHEET_KEYS.notesListOfNotes));
    this.evaluateFieldWarnings();
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Called on initial load and on form value changes
   */
  private evaluateFieldWarnings(): void {
    if (!this.schema || !this.form) return;

    // Get all form values for cross-field validations
    const allFormValues = this.form.getRawValue();

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control: SchemaControl) => {
      if ((control as any).warnings && (control as any).warnings.length > 0) {
        const formControl = this.form.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            (control as any).warnings,
            allFormValues,
            true // Always evaluate warnings
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }
  /**
   * Does this field's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet answers the
   * question the same way as every other.
   *
   * **Wired even though this sheet declares ZERO mandatory controls today.**
   * That is deliberate, and it is the lesson from roles 9, 13 and 15: they
   * were skipped during the 2026-08-11 marker rollout for having no mandatory
   * fields, and when the PO's enumerated totals list flagged their calculated
   * totals hours later the flags landed with nowhere to render — the `*`
   * silently did not appear until smoke testing caught it. A sheet that
   * renders schema-driven labels gets the marker regardless of its current
   * mandatory count, so the next list cannot reopen that gap. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves.
   * Same-sheet `selfData` is passed only by sheets keyed by bare qname; the
   * per-cell sheets (`qname[Dimension][Period]`) deliberately omit it, since a
   * same-sheet lookup would silently miss.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: SchemaControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
